import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import clsx from 'clsx';
import { getYear } from 'date-fns';
import * as R from 'ramda';

import { GET_PAY_PERIODS } from '@atom/graph/payPeriod';
import { TASK_USERS_STATUS_UPDATE } from '@atom/graph/task';
import {
  GET_TIME_ENTRIES_STATUS,
  TIME_ENTRY_CREATE,
  TIME_ENTRY_UPDATE,
} from '@atom/graph/timeEntry';
import { usePreferences } from '@atom/hooks/usePreferences';
import {
  DatePicker,
  Icon,
  IconButton,
  Progress,
  Select,
  TextField,
} from '@atom/mui';
import { CloseIcon, DoneIcon } from '@atom/mui/icons';
import { canUserCheckInToTask } from '@atom/selectors/taskSelectors';
import { userBudgetsSelector } from '@atom/selectors/userSelectors';
import { PayPeriods } from '@atom/types/payPeriod';
import { TimeEntryType } from '@atom/types/preferences';
import { Task, TaskUserStatus } from '@atom/types/task';
import { TimeEntryStatus, WorkOrderTimeEntry } from '@atom/types/timeEntry';
import { UserDetail } from '@atom/types/user';
import { WorkOrderDetailType } from '@atom/types/work';
import { numberToLocaleString } from '@atom/utilities/currencyUtility';
import { getDefaultUserId } from '@atom/utilities/timeSheetUtilities';
import {
  convertDateToMillisGMTMidday,
  convertHoursToMillis,
  convertMillisToExactHours,
  getCurrentPayPeriod,
  roundTimeEntryDuration,
} from '@atom/utilities/timeUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import classes from './userTimeEntryModal.module.css';

const { MenuItem } = Select;

const styles = {
  progress: {
    marginLeft: '12px',
  },
  typeOption: {
    textTransform: 'capitalize',
  },
};

interface Props {
  timeEntry?: WorkOrderTimeEntry;
  user: UserDetail;
  createTimeEntry?: boolean;
  setEditTimeEntryId?: (id: string) => void;
  setCreateTimeEntry?: (createTimeEntry: boolean) => void;
  refetchTimeEntries: () => void;
  workOrderDetail: WorkOrderDetailType;
  task: Task;
}

const UserTimeEntryRow = ({
  timeEntry,
  user,
  createTimeEntry,
  setEditTimeEntryId = () => {},
  setCreateTimeEntry = () => {},
  refetchTimeEntries,
  workOrderDetail,
  task,
}: Props) => {
  const preferences = usePreferences();

  const [date, setDate] = useState<Date>(
    timeEntry ? new Date(timeEntry.date) : new Date(),
  );
  const [duration, setDuration] = useState<number>(
    timeEntry ? convertMillisToExactHours(timeEntry.duration) : null,
  );
  const [selectedUserGroupId, setSelectedUserGroupId] = useState<string>(() => {
    if (timeEntry?.userGroupId) {
      return timeEntry?.userGroupId;
    }

    return getDefaultUserId(user);
  });
  const [selectedBudgetId, setSelectedBudgetId] = useState<string>(
    timeEntry ? timeEntry?.budget?.id : '',
  );
  const [selectedType, setSelectedType] = useState<TimeEntryType>(
    timeEntry ? timeEntry?.type : null,
  );
  const [payPeriods, setPayPeriods] = useState<PayPeriods>();

  const [
    getTimeEntriesStatus,
    { loading: timeEntriesStatusLoading, data: timeEntriesStatusData },
  ] = useLazyQuery(GET_TIME_ENTRIES_STATUS, {
    fetchPolicy: 'network-only',
  });

  const [getPayPeriods, { loading: payPeriodsLoading }] = useLazyQuery(
    GET_PAY_PERIODS,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
    },
  );

  const [timeEntryCreate, { loading: createLoading }] = useMutation(
    TIME_ENTRY_CREATE,
    {
      onCompleted: () => {
        refetchTimeEntries();
        setEditTimeEntryId(null);
        setCreateTimeEntry(false);
      },
    },
  );

  const [timeEntryUpdate, { loading: editLoading }] = useMutation(
    TIME_ENTRY_UPDATE,
    {
      onCompleted: () => {
        refetchTimeEntries();
        setEditTimeEntryId(null);
        setCreateTimeEntry(false);
      },
    },
  );

  const [updateUsersTaskStatus] = useMutation(TASK_USERS_STATUS_UPDATE);

  const currentPayPeriod = useMemo(() => {
    return getCurrentPayPeriod(payPeriods, date);
  }, [date, payPeriods]);

  useEffect(() => {
    const handleGetPayPeriods = async () => {
      const { data } = await getPayPeriods({
        variables: {
          input: {
            year: getYear(date),
            currentDate: convertDateToMillisGMTMidday(new Date()),
          },
        },
      });
      setPayPeriods(data?.payPeriods);
    };

    if (!isNilOrEmpty(date) && preferences?.timeTracking?.timesheet) {
      handleGetPayPeriods();
    }
  }, [date, preferences]);

  useEffect(() => {
    if (
      !isNilOrEmpty(user) &&
      !isNilOrEmpty(selectedUserGroupId) &&
      !isNilOrEmpty(currentPayPeriod)
    ) {
      getTimeEntriesStatus({
        variables: {
          input: {
            userId: user.id,
            userGroupId: selectedUserGroupId,
            dateStart: currentPayPeriod.startDate,
            dateEnd: currentPayPeriod.endDate,
          },
        },
      });
    }
  }, [selectedUserGroupId, date]);

  useEffect(() => {
    if (isNilOrEmpty(date)) {
      setSelectedUserGroupId(getDefaultUserId(user));
    }
  }, [date]);

  const budgets = userBudgetsSelector(user);
  const userGroups = user?.userGroups;

  const handleDuration = (newDuration: number) => {
    setDuration(newDuration > 0 ? newDuration : null);
  };

  const onDurationBlur = () => {
    const roundedValue = roundTimeEntryDuration(duration);

    setDuration(roundedValue);
  };

  const handleTimeEntryUpdate = async () => {
    await timeEntryUpdate({
      variables: {
        input: {
          id: timeEntry.id,
          date: convertDateToMillisGMTMidday(date),
          duration: convertHoursToMillis(duration),
          ...(preferences?.timeTracking?.timesheet && {
            userGroupId: selectedUserGroupId,
          }),
          ...(!preferences?.timeTracking?.computedBudgets && {
            budgetId: selectedBudgetId,
          }),
          ...(!isNilOrEmpty(preferences?.timeTracking?.typeEnumeration) && {
            type: selectedType,
          }),
        },
      },
    });
  };

  const handleTimeEntryCreate = async () => {
    await timeEntryCreate({
      variables: {
        input: {
          userId: user.id,
          workOrderId: workOrderDetail.id,
          taskId: task.id,
          date: convertDateToMillisGMTMidday(date),
          duration: convertHoursToMillis(duration),
          ...(preferences?.timeTracking?.timesheet && {
            userGroupId: selectedUserGroupId,
          }),
          ...(!preferences?.timeTracking?.computedBudgets &&
            !R.isEmpty(selectedBudgetId) && {
              budgetId: selectedBudgetId,
            }),
          ...(!isNilOrEmpty(preferences?.timeTracking?.typeEnumeration) && {
            type: selectedType,
          }),
        },
      },
    });

    if (canUserCheckInToTask(task, user.id)) {
      await updateUsersTaskStatus({
        variables: {
          input: {
            workOrderId: workOrderDetail.id,
            taskId: task.id,
            users: [
              {
                userId: user.id,
                status: TaskUserStatus.IN_PROGRESS,
              },
            ],
          },
        },
      });
    }
  };

  const handleCancel = () => {
    setEditTimeEntryId(null);
    setCreateTimeEntry(false);
  };

  const handleSave = async () => {
    if (createTimeEntry) {
      await handleTimeEntryCreate();
    } else {
      await handleTimeEntryUpdate();
    }
  };

  const budgetOptions = useMemo(() => {
    const options = budgets.filter(
      budget =>
        !budget.restored ||
        (budget.restored &&
          budget.reopenedWorkOrderIds.includes(workOrderDetail.id)),
    );

    return [{ id: '', name: '', rate: 0 }, ...options];
  }, [budgets, workOrderDetail]);
  const typeOptions = preferences?.timeTracking?.typeEnumeration;
  const hasTypeOptions = !isNilOrEmpty(typeOptions);

  const validationLoading = payPeriodsLoading || timeEntriesStatusLoading;
  const userGroupIsApproved =
    timeEntriesStatusData?.timeEntries?.status === TimeEntryStatus.approved;

  const isSaveDisabled =
    isNilOrEmpty(date) ||
    isNilOrEmpty(duration) ||
    validationLoading ||
    createLoading ||
    editLoading ||
    userGroupIsApproved ||
    (preferences?.timeTracking?.timesheet &&
      isNilOrEmpty(selectedUserGroupId)) ||
    (hasTypeOptions && isNilOrEmpty(selectedType));

  const hoursLabel = duration === 1 ? 'Hour' : 'Hours';

  return (
    <div className={classes.timeEntryRow}>
      <div className={classes.timeEntryDataSection}>
        <div className={classes.inputContainer}>
          <DatePicker
            label="Start Date"
            value={date}
            onChange={setDate}
            data-cy="timeEntryDateInput"
          />
        </div>
        {preferences?.timeTracking?.timesheet && (
          <div className={classes.inputContainer}>
            <Select
              fullWidth
              label="Group"
              value={selectedUserGroupId || ''}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                setSelectedUserGroupId(event.target.value)
              }
              disabled={isNilOrEmpty(date)}
              error={userGroupIsApproved}
              helperText={
                userGroupIsApproved &&
                'Cannot add time to an approved timesheet.'
              }
              data-cy="timeEntryGroupInput"
            >
              {userGroups.map(userGroup => {
                const primaryText = userGroup.groupPath.join(' / ');

                return (
                  <MenuItem key={userGroup.id} value={userGroup.id}>
                    {primaryText}
                  </MenuItem>
                );
              })}
            </Select>
          </div>
        )}
        <div className={clsx(classes.inputContainer, classes.workTime)}>
          <TextField
            type="number"
            value={duration}
            label="Work Time"
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              handleDuration(Number(event.target.value))
            }
            onBlur={onDurationBlur}
            data-cy="timeEntryTimeInput"
          />
          <div className={classes.hoursSuffix}>{hoursLabel}</div>
        </div>
        {preferences?.timeTracking?.computedBudgets ? (
          hasTypeOptions && (
            <div className={classes.inputContainer}>
              <Select
                fullWidth
                label="Wage Type"
                value={selectedType || ''}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setSelectedType(event.target.value as TimeEntryType)
                }
                data-cy="timeEntryWageInput"
              >
                {typeOptions.map(type => {
                  const primaryText = type.name;

                  return (
                    <MenuItem
                      key={type.value}
                      value={type.value}
                      style={styles.typeOption}
                    >
                      {primaryText}
                    </MenuItem>
                  );
                })}
              </Select>
            </div>
          )
        ) : (
          <div className={classes.inputContainer}>
            <Select
              fullWidth
              label="Budget"
              value={selectedBudgetId || ''}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                setSelectedBudgetId(event.target.value)
              }
              data-cy="timeEntryBudgetInput"
            >
              {budgetOptions.map(option => {
                const primaryText = option.id ? (
                  <>
                    <span>
                      {`${option.name}: ${numberToLocaleString(
                        option.rate,
                      )}/Hours `}
                    </span>
                    {option.restored && (
                      <span className={classes.snapshotSuffix}>(snapshot)</span>
                    )}
                  </>
                ) : (
                  'No Budget'
                );

                return (
                  <MenuItem key={option.id} value={option.id}>
                    {primaryText}
                  </MenuItem>
                );
              })}
            </Select>
          </div>
        )}
      </div>
      <div className={classes.timeEntryButtonSection}>
        <IconButton
          tooltip="Cancel"
          onClick={handleCancel}
          data-cy="timeEntryCancelButton"
        >
          <Icon icon={CloseIcon} />
        </IconButton>
        {validationLoading ? (
          <Progress size={20} style={styles.progress} />
        ) : (
          <IconButton
            tooltip="Save"
            onClick={handleSave}
            disabled={isSaveDisabled}
            data-cy="timeEntrySaveButton"
          >
            <Icon icon={DoneIcon} />
          </IconButton>
        )}
      </div>
    </div>
  );
};

export default UserTimeEntryRow;
